import { useQuery } from "@tanstack/react-query";
import {
  GetProjectResponse,
  ManageProjectRequest,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { queryKeys } from "./queryKeys.ts";

export const useGetManageProjectData = (projectId: string) => {
  const { getToken } = useAuthentication();
  const { data, refetch, isFetching, error } = useQuery({
    queryKey: queryKeys.manageProject(projectId),
    queryFn: async ({ signal }): Promise<ManageProjectRequest> => {
      const token = await getToken();
      if (!token) {
        throw new Error("No access token available.");
      }
      const response = await fetch(`/api/projects/${projectId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      if (!response.ok) {
        throw new Error("Server returned status outside the 200 range.");
      }
      const project = (await response.json()) as GetProjectResponse;

      return {
        id: project.project.id || "",
        displayName: project.project.displayName || "",
        projectShepherds: {
          group:
            project.project.projectShepherds
              .filter((item) => item.type === "group")
              .map((x) => x.ldapGroup) || [],
          individual:
            project.project.projectShepherds
              .filter((item) => item.type === "individual")
              .map((x) => x.uidNumber) || [],
        },
        projectMembers: {
          group:
            project.project.projectMembers
              .filter((item) => item.type === "group")
              .map((x) => x.ldapGroup) || [],
          individual:
            project.project.projectMembers
              .filter((item) => item.type === "individual")
              .map((x) => x.uidNumber) || [],
        },
        projectSigners: {
          group:
            project.project.projectSigners
              .filter((item) => item.type === "group")
              .map((x) => x.ldapGroup) || [],
          individual:
            project.project.projectSigners
              .filter((item) => item.type === "individual")
              .map((x) => x.uidNumber) || [],
        },
        infoBox: project.project.infoBox ?? "",
      };
    },
    enabled: !!projectId,
    staleTime: 10_000,
  });

  return { refetch, isFetching, error, project: data };
};
